import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

export interface DeliverableFile {
  id: string;
  deliverable_id: string;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_by: string | null;
  current_version: number;
  created_at: string;
}

export interface DeliverableFileVersion {
  id: string;
  deliverable_file_id: string;
  version_number: number;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_by: string | null;
  change_note: string | null;
  created_at: string;
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useDeliverableFiles(deliverableId: string | null) {
  return useQuery({
    queryKey: ['deliverable-files', deliverableId],
    queryFn: async () => {
      const response = await api.deliverables.files.list(deliverableId!);
      return response.files as DeliverableFile[];
    },
    enabled: !!deliverableId,
  });
}

export function useDeliverableFileVersions(deliverableId: string | null, fileId: string | null) {
  return useQuery({
    queryKey: ['deliverable-file-versions', fileId],
    queryFn: async () => {
      const response = await api.deliverables.files.listVersions(deliverableId!, fileId!);
      return response.versions as DeliverableFileVersion[];
    },
    enabled: !!deliverableId && !!fileId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useUploadDeliverableFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deliverableId, file }: { deliverableId: string; file: File }) =>
      api.deliverables.files.upload(deliverableId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deliverable-files', variables.deliverableId] });
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast.success('File uploaded');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload file');
    },
  });
}

export function useUploadDeliverableFileVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      deliverableId,
      fileId,
      file,
      changeNote,
    }: {
      deliverableId: string;
      fileId: string;
      file: File;
      changeNote?: string;
    }) => api.deliverables.files.uploadVersion(deliverableId, fileId, file, changeNote),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deliverable-files', variables.deliverableId] });
      queryClient.invalidateQueries({ queryKey: ['deliverable-file-versions', variables.fileId] });
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast.success('New version uploaded');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload version');
    },
  });
}

export function useDeleteDeliverableFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deliverableId, fileId }: { deliverableId: string; fileId: string }) =>
      api.deliverables.files.delete(deliverableId, fileId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deliverable-files', variables.deliverableId] });
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast.success('File deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete file');
    },
  });
}
